import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play } from "lucide-react";

// Self-hosted, vertical (9:16) demo video shown inside a generic phone
// bezel — intentionally NOT modeled on any specific real device (Samsung,
// Apple, etc.) to avoid borrowing another company's hardware design/logo.
// Point videoUrl at your R2 (or R2 + Cloudflare CDN) URL once exported.
//
// Usage:
//   <PhoneDemoVideo
//     videoUrl="https://REPLACE-WITH-YOUR-R2-URL/demo.mp4"
//     posterUrl="https://images.checkoutdm.com/static/demo-poster.jpg"
//     title="Checkout DM in action"
//   />
export default function PhoneDemoVideo({
  videoUrl,
  posterUrl,
  title = "Product demo",
  onPlayingChange,
}) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const updatePlaying = (value) => {
    setIsPlaying(value);
    onPlayingChange?.(value);
  };

  const handlePlay = () => {
    updatePlaying(true);
    // Video element only mounts src on click (preload="none" below), so give
    // the browser a tick before calling play().
    requestAnimationFrame(() => {
      videoRef.current?.play();
    });
  };

  return (
    <div className="relative mx-auto w-full max-w-[300px]">
      <div className="relative rounded-[2.75rem] border-[10px] border-ink bg-ink p-1.5 shadow-2xl">
        {/* speaker/camera cutout — decorative, no branding */}
        <div className="absolute left-1/2 top-2.5 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-ink" />

        <div className="relative aspect-[9/16] w-full overflow-hidden rounded-[2rem] bg-black">
          <video
            ref={videoRef}
            src={isPlaying ? videoUrl : undefined}
            poster={posterUrl}
            preload="none"
            controls={isPlaying}
            controlsList="nodownload noremoteplayback"
            disablePictureInPicture
            playsInline
            title={title}
            className="h-full w-full object-cover"
            onEnded={() => updatePlaying(false)}
          />

          <AnimatePresence>
            {!isPlaying && (
              <motion.button
                type="button"
                onClick={handlePlay}
                aria-label={`Play video: ${title}`}
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 bg-ink/20 transition-colors hover:bg-ink/30"
              >
                <span className="relative grid h-16 w-16 place-items-center">
                  {/* expanding "sonar ping" rings — pure CSS animation, no video bytes involved */}
                  <motion.span
                    className="absolute inset-0 rounded-full bg-white/50"
                    animate={{ scale: [1, 1.7], opacity: [0.6, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
                  />
                  <motion.span
                    className="absolute inset-0 rounded-full bg-white/50"
                    animate={{ scale: [1, 1.7], opacity: [0.6, 0] }}
                    transition={{
                      duration: 1.8,
                      repeat: Infinity,
                      ease: "easeOut",
                      delay: 0.6,
                    }}
                  />
                  <motion.span
                    animate={{ scale: [1, 1.06, 1] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                    className="relative grid h-16 w-16 place-items-center rounded-full bg-gradient-to-tr from-[#FF3366] via-[#FF7733] to-[#FFB347] shadow-[0_0_25px_rgba(255,51,102,0.5)]"
                  >
                    <Play size={24} className="ml-1 text-white" fill="white" />
                  </motion.span>
                </span>
                <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-ink shadow-sm">
                  Tap to watch
                </span>
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        {/* side buttons — decorative only */}
        <div className="absolute -left-[10px] top-24 h-8 w-[3px] rounded-l bg-ink/70" />
        <div className="absolute -right-[10px] top-20 h-12 w-[3px] rounded-r bg-ink/70" />
      </div>
    </div>
  );
}
